/**
 * Entity Activity Tracking Hook
 *
 * Provides polymorphic activity tracking for all entity types in the system.
 * Uses a single pattern to track views, completions, downloads, and other actions
 * across 70+ entity types without code duplication.
 *
 * Usage:
 * - useTrackEntityView: Automatically tracks when entity is viewed (useEffect)
 * - useTrackEntityAction: Manually track actions like complete, download, create
 *
 * Example:
 * ```tsx
 * useTrackEntityView({
 *   entityType: 'learning_path',
 *   entityId: pathId,
 *   entityName: path.name,
 *   metadata: { progress: path.progress }
 * });
 * ```
 */

import { useLogActivity } from '@/api/hooks';
import { useEffect } from 'react';

// All supported entity types in the system
export type EntityType =
  // Learning entities
  | 'lesson'
  | 'course'
  | 'learning_path'
  | 'module'
  | 'assessment'
  // Resource entities
  | 'asset'
  | 'template'
  | 'policy'
  // Productivity entities
  | 'task'
  | 'goal'
  | 'blocker'
  // Diagnostic entities
  | 'diagnostic'
  | 'prime_path_assessment'
  | 'gap_analysis'
  // Achievement entities
  | 'certificate'
  | 'badge'
  | 'milestone'
  // Coaching entities
  | 'coaching_session'
  | 'guidance_call'
  | 'expert'
  | 'action_plan_session'
  // Community entities
  | 'event'
  | 'workshop'
  | 'webinar'
  | 'community_message'
  | 'community_post'
  | 'post_comment'
  | 'post_reaction'
  | 'story'
  | 'recording'
  // Speedies (F-129)
  | 'speedy'
  // Alfie chat
  | 'alfie_message'
  // Navigation & search
  | 'page'
  | 'search'
  // Business entities
  | 'strategy'
  | 'business_plan';

// Actions that can be tracked
export type ActivityAction =
  | 'viewed'
  | 'completed'
  | 'downloaded'
  | 'created'
  | 'updated'
  | 'archived'
  | 'deleted'
  | 'shared'
  | 'booked'
  | 'attended'
  | 'earned'
  | 'sent'
  | 'commented'
  | 'reacted'
  | 'enrolled'
  | 'progressed'
  | 'queried'
  | 'navigated'
  // Speedies (F-129): the player, feed rows and the composer list
  | 'player_opened'
  | 'player_session_ended'
  | 'feed_row_shown'
  | 'composer_list_opened'
  | 'composer_preview_played'
  | 'composer_attached';

interface TrackEntityViewOptions {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  metadata?: Record<string, unknown>;
}

/**
 * Automatically track entity view when component mounts
 *
 * Use this hook in entity detail pages to track when user views an entity.
 * Logs activity once per mount (changes to entityId will trigger new log).
 */
export function useTrackEntityView({
  entityType,
  entityId,
  entityName,
  metadata = {},
}: TrackEntityViewOptions) {
  const { mutate: logActivity } = useLogActivity();

  useEffect(() => {
    console.log('🔍 [useTrackEntityView] Hook fired', { entityType, entityId, entityName, metadata });

    // Don't track if required fields are missing
    if (!entityId || !entityName) {
      console.log('⚠️ [useTrackEntityView] Skipping - missing entityId or entityName');
      return;
    }

    console.log('✅ [useTrackEntityView] Calling logActivity mutation');
    logActivity({
      type: getActivityTypeForEntity(entityType),
      action: 'viewed',
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      description: `Viewed ${entityType}: ${entityName}`,
      metadata,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityName, entityType, logActivity]);
}

/**
 * Manually track entity actions
 *
 * Use this for actions beyond viewing (complete, download, create, etc.)
 * Call the returned function when the action occurs.
 *
 * Example:
 * ```tsx
 * const trackAction = useTrackEntityAction();
 *
 * const handleComplete = () => {
 *   trackAction('completed', 'lesson', lessonId, lessonName, { courseId });
 *   // ... rest of completion logic
 * };
 * ```
 */
export function useTrackEntityAction() {
  const { mutate: logActivity } = useLogActivity();

  return (
    action: ActivityAction,
    entityType: EntityType,
    entityId: string,
    entityName: string,
    metadata?: Record<string, unknown>
  ) => {
    console.log('🔍 [useTrackEntityAction] Called', { action, entityType, entityId, entityName, metadata });

    // Don't track if required fields are missing
    if (!entityId || !entityName) {
      console.log('⚠️ [useTrackEntityAction] Skipping - missing entityId or entityName');
      return;
    }

    console.log('✅ [useTrackEntityAction] Calling logActivity mutation');
    logActivity({
      type: getActivityTypeForEntity(entityType),
      action,
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      description: `${capitalize(action)} ${entityType}: ${entityName}`,
      metadata: metadata || {},
    });
  };
}

/**
 * Map entity types to activity categories
 *
 * This groups entities into high-level categories for analytics and filtering.
 */
function getActivityTypeForEntity(entityType: EntityType): string {
  const typeMap: Record<EntityType, string> = {
    // Learning
    lesson: 'learning',
    course: 'learning',
    learning_path: 'learning',
    module: 'learning',
    assessment: 'learning',

    // Resources
    asset: 'resource',
    template: 'resource',
    policy: 'operations',

    // Productivity
    task: 'productivity',
    goal: 'productivity',
    blocker: 'productivity',

    // Diagnostics
    diagnostic: 'assessment',
    prime_path_assessment: 'assessment',
    gap_analysis: 'assessment',

    // Achievements
    certificate: 'achievement',
    badge: 'achievement',
    milestone: 'achievement',

    // Coaching
    coaching_session: 'coaching',
    guidance_call: 'coaching',
    expert: 'coaching',
    action_plan_session: 'coaching',

    // Community
    event: 'community',
    workshop: 'community',
    webinar: 'community',
    community_message: 'community',
    community_post: 'community',
    post_comment: 'community',
    post_reaction: 'community',
    story: 'community',
    speedy: 'speedy',
    recording: 'community',

    // Alfie chat
    alfie_message: 'alfie',

    // Navigation & search
    page: 'navigation',
    search: 'navigation',

    // Business
    strategy: 'strategy',
    business_plan: 'strategy',
  };

  return typeMap[entityType] || 'general';
}

/**
 * Capitalize first letter of string
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
